import { getAuditorSubmissionStatusPresentation } from '@/pages/screenata/policies/get-auditor-submission-status-presentation'
import type { PolicySubmissionMeta } from '@/pages/screenata/policies/types'
import { ExternalLink } from 'lucide-react'

export default function PolicySubmissionMetaBar({
  submission,
  onReview,
  showInlineCommentHint = false,
  draftVersion,
  onViewApprovedVersion,
  inline = false,
}: {
  submission: PolicySubmissionMeta
  onReview?: () => void
  showInlineCommentHint?: boolean
  draftVersion?: number | null
  onViewApprovedVersion?: () => void
  inline?: boolean
}) {
  const statusMeta = submission.submissionStatus
    ? getAuditorSubmissionStatusPresentation(submission.submissionStatus)
    : null
  const content = (
    <div className='text-text-body flex flex-wrap items-center gap-4 text-[12px]'>
      <span className='text-text-secondary font-medium'>
        {draftVersion != null ? 'Draft version' : 'Approved policy'}
      </span>
      {draftVersion != null && (
        <span>Current version: v{draftVersion} (Draft)</span>
      )}
      <span>Auditor status: {statusMeta?.metaLabel ?? 'Not reviewed'}</span>
      <span className='inline-flex items-center gap-1'>
        Approved version: v{submission.submittedVersion}
        {onViewApprovedVersion && (
          <button
            type='button'
            onClick={onViewApprovedVersion}
            className='inline-flex size-5 items-center justify-center rounded-sm text-cobalt-strong hover:bg-[#E8EEFF] focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none'
            aria-label={`View approved version v${submission.submittedVersion}`}
            title={`View approved version v${submission.submittedVersion}`}
          >
            <ExternalLink className='size-3.5' aria-hidden />
          </button>
        )}
      </span>
      {submission.auditCycleLabel && (
        <span>Audit cycle: {submission.auditCycleLabel}</span>
      )}
      {submission.submittedAt && (
        <span>
          {submission.submittedBy ? 'Submitted on' : 'Approved on'}:{' '}
          {new Date(submission.submittedAt).toLocaleString()}
        </span>
      )}
      {submission.submittedBy && (
        <span>Submitted by: {submission.submittedBy}</span>
      )}
      {submission.submissionReviewedAt && (
        <span>
          {submission.submissionStatus === 'NEEDS_REVISION'
            ? 'Reviewed by auditor'
            : 'Approved by auditor'}
          : {new Date(submission.submissionReviewedAt).toLocaleString()}
          {submission.submissionReviewedBy
            ? ` · ${submission.submissionReviewedBy}`
            : ''}
        </span>
      )}
      {submission.submissionStatus === 'NEEDS_REVISION' &&
        submission.submissionReviewComments && (
          <span
            className='text-text-subtle w-full leading-relaxed break-words whitespace-pre-wrap'
            title={submission.submissionReviewComments}
          >
            Comment: {submission.submissionReviewComments}
          </span>
        )}
      {showInlineCommentHint && submission.submissionId && (
        <span className='text-text-subtle'>
          Select text to add inline comments.
        </span>
      )}
      {onReview &&
        (!submission.submissionStatus ||
          submission.submissionStatus === 'SUBMITTED') && (
          <button
            type='button'
            onClick={onReview}
            className='bg-primary text-primary-foreground hover:bg-primary/90 ml-auto inline-flex h-7 items-center rounded-sm px-2.5 text-[12px] font-medium transition-colors'
          >
            Review policy
          </button>
        )}
    </div>
  )

  if (inline) return content

  return (
    <div className='border-border bg-surface-subtle border-b px-6 py-3'>
      {content}
    </div>
  )
}
